/**
 * Promotion Template List Page
 *
 * Shows all Promotion Tier Templates as cards with status, tier summary,
 * and item/group linkage. Empty state with "Create Template" CTA.
 *
 * Part of Plan 199 (SaaS Promotion Tiers), Epic 02.
 */

import { useNavigate } from "react-router-dom";
import { Plus, ArrowLeft, Percent, ChevronRight } from "lucide-react";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  usePromotionTemplates,
  type PromotionTemplateSummary,
} from "./api/promotionTierHooks";

export function PromotionTemplateListPage() {
  const navigate = useNavigate();
  const { data: templates, isLoading, error } = usePromotionTemplates();

  return (
    <PageContainer>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full"
              onClick={() => navigate("/storefront/settings")}
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h2 className="text-2xl font-semibold">Promotion Tiers</h2>
              <p className="text-sm text-muted-foreground">
                Configure quantity-based tiered promotions
              </p>
            </div>
          </div>
          <Button
            onClick={() => navigate("/storefront/promotions/new")}
            size="sm"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Create Template
          </Button>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-24 rounded-2xl" />
            ))}
          </div>
        ) : error ? (
          <Card className="rounded-2xl border-0">
            <CardContent className="p-8 text-center">
              <p className="text-sm text-muted-foreground">
                Failed to load templates. Please try again.
              </p>
            </CardContent>
          </Card>
        ) : !templates || templates.length === 0 ? (
          <EmptyState onCreateClick={() => navigate("/storefront/promotions/new")} />
        ) : (
          <div className="space-y-3">
            {templates.map((template) => (
              <TemplateCard
                key={template.name}
                template={template}
                onClick={() =>
                  navigate(`/storefront/promotions/${template.name}`)
                }
              />
            ))}
          </div>
        )}
      </div>
    </PageContainer>
  );
}

function EmptyState({ onCreateClick }: { onCreateClick: () => void }) {
  return (
    <Card className="rounded-2xl border-0">
      <CardContent className="p-12 text-center space-y-4">
        <div className="w-12 h-12 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
          <Percent className="w-6 h-6 text-primary" />
        </div>
        <div>
          <p className="font-medium text-sm">No promotion tiers yet</p>
          <p className="text-xs text-muted-foreground mt-1">
            Create your first tier template to reward bulk buyers with
            escalating discounts and perks.
          </p>
        </div>
        <Button onClick={onCreateClick} size="sm">
          <Plus className="w-4 h-4 mr-1.5" />
          Create Template
        </Button>
      </CardContent>
    </Card>
  );
}

function TemplateCard({
  template,
  onClick,
}: {
  template: PromotionTemplateSummary;
  onClick: () => void;
}) {
  const appliesTo =
    template.applies_to === "Item Code"
      ? `Item: ${template.apply_on_item || "—"}`
      : `Item Group: ${template.apply_on_item_group || "—"}`;

  return (
    <Card
      className="rounded-2xl border-0 shadow-apple-sm hover:bg-muted/50 transition-colors cursor-pointer"
      onClick={onClick}
    >
      <CardContent className="p-5 flex items-center justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm truncate">
              {template.template_name}
            </span>
            <span
              className={`w-2 h-2 rounded-full flex-shrink-0 ${
                template.enabled ? "bg-green-500" : "bg-gray-300"
              }`}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-1 truncate">
            {appliesTo} · {template.tier_count} tier
            {template.tier_count !== 1 ? "s" : ""} · {template.perk_count} perk
            {template.perk_count !== 1 ? "s" : ""}
          </p>
        </div>
        <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0 ml-3" />
      </CardContent>
    </Card>
  );
}
